import fireStationBanner from '../images/fire-station-event-banner.png';
import outreachBanner from '../images/town-hall-outreach-banner.png';
import motorcyclesBanner from '../images/police-motorcycles-banner.png';
import stationVisitBanner from '../images/station-visit-banner.png';
import holidayBanner from '../images/holiday-community-banner.png';
import halloweenPatrolBanner from '../images/halloween-patrol-banner.png';
import trunkTreatBanner from '../images/trunk-or-treat-banner.png';
import officerGroupBanner from '../images/officer-group-banner.png';
import coffeeBanner from '../images/community-coffee-banner.png';
import breakfastBanner from '../images/community-breakfast-banner.png';
import communityBanner from '../images/community-event-banner.png';
import dispatchBanner from '../images/dispatch-center-banner.png';
import policeStationBanner from '../images/police-station-banner.png';
import k9Banner from '../images/k9-unit-banner.png';
import {ReportPageHeader, ReportChapterHeader} from "../../../shared/ui/layout/ReportPage";
import OffenseDensityMap from './OffenseDensityMap';
import {useSharedFilter, shouldOpenSharedSection} from '../../../shared/navigation/useSharedReport';
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles";
import { PieChartWithToggles } from "../../../shared/ui/chart/PieChartWithToggles.jsx";
import { HoverPopover } from "../../../shared/ui/overlay/HoverPopover.jsx";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { CrimeTrendInsights, TrendPanel } from "./CrimeTrendInsights";
import { TrendDateRange } from "./TrendDateRange";
import { comparisonRange } from "../utils/crimeTrendAnalysis";
import BudgetCard from "../../townBudget/components/budgetCard";
import { db } from "../../../firebase";
import {
  availableYears,
  buildCrimeSeries,
  compareYears,
  completeMonthCount,
  firstObservedMonth,
  monthSpanLabel,
  titleCaseCallType,
  totalForPeriod,
} from "../utils/crimeData";
import {
  OFFENSE_MAP_COLLECTION,
  OFFENSE_MAP_INDEX,
  availableReportYears,
  decodeOffenseDocuments,
  filterOffensePoints,
  formatWallTime,
  groupOffenseLocations,
  groupedMetricForYears,
  offenseCatalog,
  offenseDocumentIds,
  totalMetricForYears,
} from "../utils/crimeOffenseData";
import { CRIME_SECTIONS, crimeSectionFromSearch } from "../model/crimeSections";
import {
  CFS_COLLECTION,
  CFS_INDEX,
  callsCatalog,
  callsDocumentIds,
  decodeCallsDocuments,
  filterCallsPoints,
  groupCallsLocations,
} from "../utils/crimeCallsForServiceData";
import { describeCallType } from "../utils/crimeCallTypeDescriptions";
import { describeOffense, NIBRS_DEFINITIONS_URL } from "../utils/crimeOffenseDefinitions";
import {
  BLOTTER_DAYS_COLLECTION,
  BLOTTER_META_COLLECTION,
  BLOTTER_META_ID,
  daysInclusive,
  decodeBlotterDays,
  shiftDay,
} from "../utils/crimePoliceBlotterData";
import {
  STOLEN_VEHICLE_COLLECTION,
  STOLEN_VEHICLE_META_COLLECTION,
  STOLEN_VEHICLE_META_ID,
  decodeStolenVehicleYears,
} from "../utils/crimeStolenVehicleData";

const SUMMARY_COLLECTION = "POLICE_SUMMARIES";
const SUMMARY_DOCUMENT = "blotter-call-types-by-month";
const MAX_BLOTTER_DAYS = 365;
const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TREND_COLORS = ["#1d4ed8", "#dc2626", "#059669", "#7c3aed", "#d97706", "#0891b2", "#be123c"];

const numericPrefix = (value) => Number(String(value).match(/^\D*(\d+)/)?.[1] ?? 999);
const orderedGroups = (items) => [...items].sort((left, right) => {
  const numeric = numericPrefix(left.label) - numericPrefix(right.label);
  return numeric || left.label.localeCompare(right.label, undefined, { numeric: true });
});

function groupedBarSeries(items, key, label) {
  return {
    labels: items.map((item) => item.label),
    series: [{ key, label, valueType: "count", color: "#1e3a8a", data: items.map((item) => item.value) }],
  };
}

function FitOffenseMap({ locations }) {
  const map = useMap();
  useEffect(() => {
    if (!locations.length) return;
    map.fitBounds(locations.map((location) => [location.latitude, location.longitude]), { padding: [28, 28], maxZoom: 15 });
  }, [locations, map]);
  return null;
}

function OffenseMap({ locations }) {
  return (
    <div className="h-[280px] sm:h-[430px] overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-inner" data-testid="offense-map">
      <MapContainer center={[41.701, -72.59]} zoom={12} preferCanvas className="h-full w-full" scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitOffenseMap locations={locations} />
        {locations.map((location) => (
          <CircleMarker
            key={`${location.latitude},${location.longitude}`}
            center={[location.latitude, location.longitude]}
            radius={Math.min(14, 5 + Math.log2(location.count + 1) * 2)}
            pathOptions={{ color: "#1e3a8a", fillColor: "#2563eb", fillOpacity: 0.68, weight: 1.5 }}
          >
            <Popup>
              <div className="min-w-44 text-sm">
                <div className="font-semibold text-slate-950">{location.latest.streetName || "Location not labeled"}</div>
                <div>{location.count.toLocaleString()} reported {location.count === 1 ? "offense" : "offenses"}</div>
                <div className="mt-1 text-xs text-slate-600">Latest: {formatWallTime(location.latest.receivedWallTimeMs)}</div>
                {location.latest.callType && <div className="text-xs text-slate-600">Call: {location.latest.callType}</div>}
                {location.latest.disposition && <div className="text-xs text-slate-600">Disposition: {location.latest.disposition}</div>}
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

function MetricCard({ label, value, explanation }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <span>{label}</span>
        <HoverPopover
          maxWidth={360}
          popoverClassName="p-4 text-left normal-case tracking-normal text-slate-700"
          content={(
            <div>
              <div className="font-semibold text-slate-950">{label}</div>
              <p className="mt-1 text-sm leading-relaxed">{explanation}</p>
            </div>
          )}
        >
          <InformationCircleIcon
            className="h-4 w-4 cursor-help text-slate-400 hover:text-blue-700"
            aria-label={`Explain ${label.toLowerCase()}`}
          />
        </HoverPopover>
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-950">{value.toLocaleString()}</div>
    </div>
  );
}

function CallsForServiceMap({ locations }) {
  return (
    <div className="h-[280px] sm:h-[430px] overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-inner" data-testid="calls-for-service-map">
      <MapContainer center={[41.701, -72.59]} zoom={12} preferCanvas className="h-full w-full" scrollWheelZoom={false}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitOffenseMap locations={locations} />
        {locations.map((location) => (
          <CircleMarker key={`${location.latitude},${location.longitude}`} center={[location.latitude, location.longitude]}
            radius={Math.min(14, 5 + Math.log2(location.count + 1) * 2)}
            pathOptions={{ color: "#1e3a8a", fillColor: "#2563eb", fillOpacity: 0.68, weight: 1.5 }}>
            <Popup><div className="min-w-44 text-sm">
              <div className="font-semibold text-slate-950">{titleCaseCallType(location.streetName) || "Location not labeled"}</div>
              <div>{location.count.toLocaleString()} published {location.count === 1 ? "call" : "calls"}</div>
              {location.latest?.receivedWallTimeMs != null && <div className="mt-1 text-xs text-slate-600">Latest: {formatWallTime(location.latest.receivedWallTimeMs)}</div>}
              {location.latest?.callType && <div className="text-xs text-slate-600">Call: {titleCaseCallType(location.latest.callType)}</div>}
            </div></Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

function CallsForServiceReport() {
  const [index, setIndex] = useState(null), [selectedId, setSelectedId] = useSharedFilter("CrimeDashboard.jsx:CallsForServiceReport:selectedId", "");
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [points, setPoints] = useState([]), [status, setStatus] = useState("loading-index"), [error, setError] = useState("");
  const [fromYear, setFromYear] = useSharedFilter("CrimeDashboard.jsx:CallsForServiceReport:fromYear", null), [throughYear, setThroughYear] = useSharedFilter("CrimeDashboard.jsx:CallsForServiceReport:throughYear", null);
  const catalog = useMemo(() => callsCatalog(index), [index]);
  const selected = catalog.find(entry => entry.id === selectedId);

  useEffect(() => {
    let active = true;
    getDoc(doc(db, CFS_COLLECTION, CFS_INDEX)).then(snapshot => {
      if (!snapshot.exists()) throw new Error("Published Calls for Service index was not found");
      if (!active) return;
      const value = snapshot.data(), entries = callsCatalog(value);
      setIndex(value); setSelectedId.initialize(entries[0]?.id || ""); setStatus("idle");
    }).catch(requestError => { if (active) { setError(requestError.message); setStatus("error"); } });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selected) return;
    let active = true; setStatus("loading-points"); setError("");
    Promise.all(callsDocumentIds(selected).map(documentId => getDoc(doc(db, CFS_COLLECTION, documentId))))
      .then(snapshots => {
        if (snapshots.some(snapshot => !snapshot.exists())) throw new Error("One or more Calls for Service map parts are missing");
        if (!active) return;
        const summary = snapshots[0].data();
        const years = Object.keys(summary.totalsByYear || {}).map(Number).sort((a, b) => a - b);
        setSelectedSummary(summary);
        setPoints(decodeCallsDocuments(snapshots.slice(1).map(snapshot => snapshot.data())));
        setFromYear.initialize(years[0] ?? null); setThroughYear.initialize(years.at(-1) ?? null); setStatus("loaded");
      }).catch(requestError => { if (active) { setError(requestError.message); setStatus("error"); } });
    return () => { active = false; };
  }, [selected]);

  const years = useMemo(() => Object.keys(selectedSummary?.totalsByYear || {}).map(Number).sort((a, b) => a - b), [selectedSummary]);
  const filtered = useMemo(() => filterCallsPoints(points, fromYear, throughYear), [points, fromYear, throughYear]);
  const locations = useMemo(() => groupCallsLocations(filtered), [filtered]);
  const total = useMemo(() => totalMetricForYears(selectedSummary?.totalsByYear, fromYear, throughYear), [selectedSummary, fromYear, throughYear]);
  const months = useMemo(() => orderedGroups(groupedMetricForYears(selectedSummary?.byMonthByYear, fromYear, throughYear)), [selectedSummary, fromYear, throughYear]);
  const weekdays = useMemo(() => orderedGroups(groupedMetricForYears(selectedSummary?.byWeekdayByYear, fromYear, throughYear)), [selectedSummary, fromYear, throughYear]);
  const annualTotals = useMemo(() => {
    if (fromYear == null || throughYear == null || throughYear < fromYear) return [];
    return Array.from({ length: throughYear - fromYear + 1 }, (_, index) => {
      const year = fromYear + index;
      return { label: String(year), value: Number(selectedSummary?.totalsByYear?.[year] || 0) };
    });
  }, [selectedSummary, fromYear, throughYear]);
  const monthChart = useMemo(() => groupedBarSeries(months, "calls-by-month", "Calls"), [months]);
  const weekdayChart = useMemo(() => groupedBarSeries(weekdays, "calls-by-weekday", "Calls"), [weekdays]);
  const yearChart = useMemo(() => groupedBarSeries(annualTotals, "calls-by-year", "Calls"), [annualTotals]);
  const tableRows = useMemo(() => filtered.map((point, index) => ({
    id: `${selectedId}-${point.year}-${point.latitude}-${point.longitude}-${index}`,
    year: String(point.year),
    receivedWallTimeMs: point.receivedWallTimeMs,
    street: titleCaseCallType(point.streetName) || "—",
    count: point.count,
  })), [filtered, selectedId]);
  const tableColumns = useMemo(() => [
    { key: "year", label: "Year", headerAlign: "left", style: { width: "100px" } },
    { key: "receivedWallTimeMs", label: "Latest received", headerAlign: "left", style: { width: "190px" }, render: (row) => row.receivedWallTimeMs == null ? "—" : formatBlotterDateTime(row.receivedWallTimeMs) },
    { key: "street", label: "Street", headerAlign: "left", style: { width: "240px" } },
    { key: "count", label: "Published calls", headerAlign: "right", align: "right", style: { width: "150px" }, render: (row) => row.count.toLocaleString() },
  ], []);
  const callTypeDescription = useMemo(() => describeCallType(selectedSummary?.callType || selected?.name), [selectedSummary, selected]);

  return (
    <section className="space-y-8 sm:space-y-10">
      <div className="space-y-3 sm:space-y-4">
        <img src={policeStationBanner} alt="Watercolor illustration of the Glastonbury police station" className="h-auto w-full object-contain" />
        <ReportPageHeader editorial title="Calls for Service" headingAs="h1" />
      </div>
      <p className="max-w-5xl text-base leading-7 text-slate-600">Explore the calls for service published by Glastonbury Police. Choose a call type and year range to see reported locations, compare activity by month and weekday, and review the underlying entries. Calls for service describe police activity and requests for assistance; they do not necessarily indicate a crime.</p>
      <ReportChapterHeader headingAs="h2" title="Calls for Service & Locations" tone="lavender"
        description="Use the filters below to explore a category over time. The maps show published locations, with calls at the same coordinates grouped together. Some calls have withheld or missing locations and will not appear on the maps." />
      <div className="space-y-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-end">
          <label className="text-sm font-semibold text-slate-800">Call type
            <select aria-label="Call type" value={selectedId} onChange={event => setSelectedId(event.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal shadow-sm">
              {catalog.map(entry => <option key={entry.id} value={entry.id}>{titleCaseCallType(entry.name)}</option>)}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-800">From year
            <select aria-label="Calls from year" value={fromYear ?? ""} onChange={event => setFromYear(Number(event.target.value))} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal">{years.map(year => <option key={year}>{year}</option>)}</select>
          </label>
          <label className="text-sm font-semibold text-slate-800">Through year
            <select aria-label="Calls through year" value={throughYear ?? ""} onChange={event => setThroughYear(Number(event.target.value))} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal">{years.map(year => <option key={year} disabled={fromYear != null && year < fromYear}>{year}</option>)}</select>
          </label>
        </div>
        {status.startsWith("loading") && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">Loading Calls for Service data…</div>}
        {status === "error" && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">Calls for Service could not be loaded: {error}</div>}
        {status === "loaded" && <>
          <div className="grid gap-3 sm:grid-cols-2">
            <MetricCard label="Total calls for service" value={total} explanation="The Police Department’s published count of calls in the selected category and years." />
            <MetricCard label="Distinct map points" value={locations.length} explanation="The number of unique published coordinates. Multiple calls at the same coordinates are combined, and calls with withheld or missing locations are excluded." />
          </div>
          <section className="rounded-lg border border-slate-200 bg-slate-50 px-5 py-4" aria-labelledby="call-type-description-heading">
            <h2 id="call-type-description-heading" className="text-lg font-semibold text-slate-950">About {titleCaseCallType(selectedSummary?.callType || selected?.name)}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">{callTypeDescription.meaning}</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-700"><span className="font-semibold text-slate-900">Legal context:</span> {callTypeDescription.legalContext}</p>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <a href={callTypeDescription.dispatchCenterUrl} target="_blank" rel="noreferrer" className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-900">Glastonbury Police Dispatch Center</a>
              {(callTypeDescription.sources || []).map((source) => <a key={source.href} href={source.href} target="_blank" rel="noreferrer" className="font-medium text-teal-700 underline underline-offset-2 hover:text-teal-900">{source.label}</a>)}
            </div>
          </section>
          {locations.length ? <CallsForServiceMap locations={locations} /> : <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-700">The Police Department does not publish map coordinates for this call type and time period.</div>}
          <OffenseDensityMap points={filtered} category={titleCaseCallType(selected?.name)} callsForService />
          <div className="grid gap-4 xl:grid-cols-2">
            <LineChartWithToggles labels={monthChart.labels} series={monthChart.series} title="Calls for Service by Month" showSeriesToggles={false} showChartTypeSelector={false} showLegend={false} initialChartType="bar" defaultYAxisMode="zero" xAxisTitle="Received month" yAxisTitle="Calls" />
            <LineChartWithToggles labels={weekdayChart.labels} series={weekdayChart.series} title="Calls for Service by Day of the Week" showSeriesToggles={false} showChartTypeSelector={false} showLegend={false} initialChartType="bar" defaultYAxisMode="zero" xAxisTitle="Received day" yAxisTitle="Calls" />
          </div>
          <LineChartWithToggles labels={yearChart.labels} series={yearChart.series} title="Calls for Service by Year" showSeriesToggles={false} showChartTypeSelector={false} showLegend={false} initialChartType="bar" defaultYAxisMode="zero" xAxisTitle="Received year" yAxisTitle="Calls" />
          <img src={k9Banner} alt="Watercolor illustration of a Glastonbury K-9 officer, police dog, and patrol vehicle" className="h-auto w-full object-contain" loading="lazy" />
          <section className="overflow-hidden rounded-lg border border-slate-200">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3"><h2 className="text-lg font-semibold text-slate-950">Calls for Service Entries</h2></div>
            <div className="p-2">
              <CommonTable
                columns={tableColumns}
                data={tableRows}
                headerColor="bg-slate-100"
                showAggregateTotal={false}
                filterEmptyRows={false}
                defaultSortField="receivedWallTimeMs"
                defaultSortOrder="desc"
              />
            </div>
          </section>

        </>}
      </div>
    </section>
  );
}

function OffenseReport() {
  const [index, setIndex] = useState(null);
  const [selectedId, setSelectedId] = useSharedFilter("CrimeDashboard.jsx:OffenseReport:selectedId", "");
  const [points, setPoints] = useState([]);
  const [status, setStatus] = useState("loading-index");
  const [error, setError] = useState("");
  const [fromYear, setFromYear] = useSharedFilter("CrimeDashboard.jsx:OffenseReport:fromYear", null);
  const [throughYear, setThroughYear] = useSharedFilter("CrimeDashboard.jsx:OffenseReport:throughYear", null);
  const catalog = useMemo(() => offenseCatalog(index).filter((entry) => entry.parts > 0), [index]);
  const selected = catalog.find((entry) => entry.id === selectedId);

  useEffect(() => {
    let active = true;
    getDoc(doc(db, OFFENSE_MAP_COLLECTION, OFFENSE_MAP_INDEX))
      .then((snapshot) => {
        if (!snapshot.exists()) throw new Error("Published offense map index was not found");
        if (!active) return;
        const value = snapshot.data();
        const entries = offenseCatalog(value).filter((entry) => entry.parts > 0);
        const preferred = entries.find((entry) => entry.name === "Aggravated Assault") || entries[0];
        setIndex(value);
        setSelectedId.initialize(preferred?.id || entries[0]?.id || "");
        setStatus("idle");
      })
      .catch((requestError) => { if (active) { setError(requestError.message); setStatus("error"); } });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedId || !selected) return;
    if (!selected.parts) {
      setPoints([]); setFromYear.initialize(null); setThroughYear.initialize(null); setStatus("loaded"); return;
    }
    let active = true;
    setStatus("loading-points"); setError("");
    Promise.all(offenseDocumentIds(selected).map((documentId) => getDoc(doc(db, OFFENSE_MAP_COLLECTION, documentId))))
      .then((snapshots) => {
        if (snapshots.some((snapshot) => !snapshot.exists())) throw new Error("One or more offense map parts are missing");
        const decoded = decodeOffenseDocuments(snapshots.map((snapshot) => snapshot.data()));
        const years = availableReportYears(decoded, selected);
        if (!active) return;
        setPoints(decoded); setFromYear.initialize(years[0] ?? null); setThroughYear.initialize(years.at(-1) ?? null); setStatus("loaded");
      })
      .catch((requestError) => { if (active) { setError(requestError.message); setStatus("error"); } });
    return () => { active = false; };
  }, [selectedId, selected]);

  const years = useMemo(() => availableReportYears(points, selected), [points, selected]);
  const filtered = useMemo(() => fromYear == null || throughYear == null ? [] : filterOffensePoints(points, fromYear, throughYear), [points, fromYear, throughYear]);
  const locations = useMemo(() => groupOffenseLocations(filtered), [filtered]);
  const totalOffenses = useMemo(() => totalMetricForYears(selected?.totalOffensesByYear, fromYear, throughYear), [selected, fromYear, throughYear]);
  const cleared = useMemo(() => totalMetricForYears(selected?.clearedByArrestByYear, fromYear, throughYear), [selected, fromYear, throughYear]);
  const monthItems = useMemo(() => orderedGroups(groupedMetricForYears(selected?.offensesByMonthByYear, fromYear, throughYear)), [selected, fromYear, throughYear]);
  const weekdayItems = useMemo(() => orderedGroups(groupedMetricForYears(selected?.offensesByWeekdayByYear, fromYear, throughYear)), [selected, fromYear, throughYear]);
  const ageItems = useMemo(() => orderedGroups(groupedMetricForYears(selected?.arrestsByAgeRangeByYear, fromYear, throughYear)), [selected, fromYear, throughYear]);
  const annualOffenses = useMemo(() => {
    if (fromYear == null || throughYear == null || throughYear < fromYear) return [];
    return Array.from({ length: throughYear - fromYear + 1 }, (_, index) => {
      const year = fromYear + index;
      return { label: String(year), value: Number(selected?.totalOffensesByYear?.[year] || 0) };
    });
  }, [selected, fromYear, throughYear]);
  const monthChart = useMemo(() => groupedBarSeries(monthItems, "monthly-offenses", "Offenses"), [monthItems]);
  const weekdayChart = useMemo(() => groupedBarSeries(weekdayItems, "weekday-offenses", "Offenses"), [weekdayItems]);
  const yearChart = useMemo(() => groupedBarSeries(annualOffenses, "annual-offenses", "Offenses"), [annualOffenses]);
  const tableRows = useMemo(() => filtered.map((point, index) => ({
    id: `${selectedId}-${point.cfsNumber || "offense"}-${point.receivedWallTimeMs}-${index}`,
    receivedWallTimeMs: point.receivedWallTimeMs,
    cfsNumber: point.cfsNumber || "—",
    street: titleCaseCallType(point.streetName) || "—",
    callType: titleCaseCallType(point.callType) || "—",
    disposition: point.disposition || "—",
  })), [filtered, selectedId]);
  const tableColumns = useMemo(() => [
    { key: "receivedWallTimeMs", label: "Received", headerAlign: "left", style: { width: "190px" }, render: (row) => formatBlotterDateTime(row.receivedWallTimeMs) },
    { key: "cfsNumber", label: "CFS number", headerAlign: "left", style: { width: "160px" } },
    { key: "street", label: "Street", headerAlign: "left", style: { width: "220px" } },
    { key: "callType", label: "Call type", headerAlign: "left", style: { width: "220px" } },
    { key: "disposition", label: "Disposition", headerAlign: "left", style: { width: "220px" } },
  ], []);
  const offenseDescription = useMemo(() => describeOffense(selected?.name), [selected]);

  return (
    <section className="space-y-8 sm:space-y-10">
      <div className="space-y-3 sm:space-y-4">
        <img src={communityBanner} alt="Watercolor illustration of a community dunk-tank event on a Glastonbury town green" className="h-auto w-full object-contain" />
        <ReportPageHeader editorial title="Offenses" headingAs="h1" />
      </div>
      <p className="max-w-5xl text-base leading-7 text-slate-600">Explore offense records published by Glastonbury Police. Choose a category and year range to see reported locations, compare activity over time, and review individual entries. One incident can include multiple offenses, so offense totals are not counts of unique incidents or people.</p>
      <ReportChapterHeader headingAs="h2" title="Reported Offenses & Locations" tone="lavender"
        description="Use the filters below to explore an offense category. Reports at the same coordinates share a map marker. Records with missing or withheld locations are excluded from the maps." />
      <div className="space-y-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-end">
          <label className="text-sm font-semibold text-slate-800">
            Offense category
            <select aria-label="Offense category" value={selectedId} onChange={(event) => setSelectedId(event.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal shadow-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200">
              {catalog.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-800">From year
            <select aria-label="Offense map from year" value={fromYear ?? ""} onChange={(event) => setFromYear(Number(event.target.value))} disabled={!years.length} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal disabled:bg-slate-100">
              {years.map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-800">Through year
            <select aria-label="Offense map through year" value={throughYear ?? ""} onChange={(event) => setThroughYear(Number(event.target.value))} disabled={!years.length} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal disabled:bg-slate-100">
              {years.map((year) => <option key={year} value={year} disabled={fromYear != null && year < fromYear}>{year}</option>)}
            </select>
          </label>
        </div>

        {status.startsWith("loading") && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">Loading published offense locations…</div>}
        {status === "error" && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">Offense locations could not be loaded: {error}</div>}
        {status === "loaded" && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <MetricCard
                label="Total offenses reported"
                value={totalOffenses}
                explanation="The Police Department’s published count of offense records in the selected category and years. One incident can contain multiple offenses, so this is an offense count rather than a count of unique incidents."
              />
              <MetricCard
                label="Distinct map points"
                value={locations.length}
                explanation="The number of unique published latitude-and-longitude points shown on the map. Multiple offense reports at the same coordinates are combined into one marker, and reports with missing or withheld locations do not appear."
              />
              <MetricCard
                label="Cleared by arrest"
                value={cleared}
                explanation="The Police Department’s published count of offense reports marked “Cleared by Arrest” in the selected category and years. This counts cleared offense reports, not the number of people arrested."
              />
            </div>
        {selected && (
          <section className="rounded-lg border border-slate-200 bg-slate-50 px-5 py-4" aria-labelledby="offense-description-heading">
            <h2 id="offense-description-heading" className="text-lg font-semibold text-slate-950">About {selected.name}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">{offenseDescription.definition}</p>
            {offenseDescription.isNibrsDefinition && (
              <p className="mt-2 text-xs leading-relaxed text-slate-500">This FBI NIBRS definition is a reporting classification and does not define a criminal charge under Connecticut law.</p>
            )}
            <a href={NIBRS_DEFINITIONS_URL} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-medium text-teal-700 underline underline-offset-2 hover:text-teal-900">Source: FBI NIBRS Offense Definitions</a>
          </section>
        )}
            {filtered.length > 0
              ? <OffenseMap locations={locations} />
              : <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-700">The Police Department does not publish map coordinates for this offense and time period.</div>}
            <OffenseDensityMap points={filtered} category={selected?.name} fromYear={fromYear} throughYear={throughYear} />
            <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
              <div className="space-y-4">
                <LineChartWithToggles
                  labels={monthChart.labels}
                  series={monthChart.series}
                  title="Offenses Reported by Month"
                  showSeriesToggles={false}
                  showChartTypeSelector={false}
                  showLegend={false}
                  initialChartType="bar"
                  defaultYAxisMode="zero"
                  xAxisTitle="Received month"
                  yAxisTitle="Offenses reported"
                />
                <LineChartWithToggles
                  labels={weekdayChart.labels}
                  series={weekdayChart.series}
                  title="Offenses Reported by Day of the Week"
                  showSeriesToggles={false}
                  showChartTypeSelector={false}
                  showLegend={false}
                  initialChartType="bar"
                  defaultYAxisMode="zero"
                  xAxisTitle="Received day"
                  yAxisTitle="Offenses reported"
                />
              </div>
              <PieChartWithToggles
                title="Arrests by Age Group"
                items={ageItems.map((item) => ({ key: item.label, label: item.label, value: item.value, valueType: "count" }))}
                showSeriesToggles={false}
                showChartTypeSelector={false}
                showLegend={false}
                showOutsideLabels
                titleBar
                sliceOrder="input"
              />
            </div>
            <LineChartWithToggles
              labels={yearChart.labels}
              series={yearChart.series}
              title="Offenses Reported by Year"
              showSeriesToggles={false}
              showChartTypeSelector={false}
              showLegend={false}
              initialChartType="bar"
              defaultYAxisMode="zero"
              xAxisTitle="Received year"
              yAxisTitle="Offenses reported"
            />
            <img src={dispatchBanner} alt="Watercolor illustration of a Glastonbury emergency dispatcher at a communications workstation" className="h-auto w-full object-contain" loading="lazy" />
            <section className="overflow-hidden rounded-lg border border-slate-200">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3"><h2 className="text-lg font-semibold text-slate-950">Offense Entries</h2></div>
              <div className="p-2">
                <CommonTable
                  columns={tableColumns}
                  data={tableRows}
                  headerColor="bg-slate-100"
                  showAggregateTotal={false}
                  filterEmptyRows={false}
                  defaultSortField="receivedWallTimeMs"
                  defaultSortOrder="desc"
                />
              </div>
            </section>
          </>
        )}

      </div>
    </section>
  );
}

function formatBlotterDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  }).format(date);
}

function PoliceBlotterReport() {
  const [metadata, setMetadata] = useState(null);
  const [fromDay, setFromDay] = useSharedFilter("CrimeDashboard.jsx:PoliceBlotterReport:fromDay", "");
  const [throughDay, setThroughDay] = useSharedFilter("CrimeDashboard.jsx:PoliceBlotterReport:throughDay", "");
  const [records, setRecords] = useState([]);
  const [callType, setCallType] = useSharedFilter("CrimeDashboard.jsx:PoliceBlotterReport:callType", "");
  const [street, setStreet] = useSharedFilter("CrimeDashboard.jsx:PoliceBlotterReport:street", "");
  const [status, setStatus] = useState("loading-index");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getDoc(doc(db, BLOTTER_META_COLLECTION, BLOTTER_META_ID)).then((snapshot) => {
      if (!snapshot.exists()) throw new Error("Published Police Blotter index was not found");
      if (!active) return;
      const value = snapshot.data();
      setMetadata(value);
      setFromDay.initialize(shiftDay(value.lastDay, -6));
      setThroughDay.initialize(value.lastDay);
      setStatus("idle");
    }).catch((requestError) => {
      if (active) { setError(requestError.message); setStatus("error"); }
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!metadata || !fromDay || !throughDay || fromDay > throughDay) return;
    const days = daysInclusive(fromDay, throughDay, MAX_BLOTTER_DAYS + 1);
    if (days.length > MAX_BLOTTER_DAYS || days.at(-1) !== throughDay) {
      setError("Choose a date range of 365 days or fewer.");
      setStatus("error");
      return;
    }
    let active = true;
    setStatus("loading-records"); setError("");
    Promise.all(days.map((day) => getDoc(doc(db, BLOTTER_DAYS_COLLECTION, day))))
      .then((snapshots) => {
        if (!active) return;
        const publishedDays = snapshots.filter((snapshot) => snapshot.exists()).map((snapshot) => snapshot.data());
        setRecords(decodeBlotterDays(publishedDays).sort((left, right) => right.receivedDateTime.localeCompare(left.receivedDateTime)));
        setCallType.initialize(""); setStreet.initialize(""); setStatus("loaded");
      })
      .catch((requestError) => {
        if (active) { setError(requestError.message); setStatus("error"); }
      });
    return () => { active = false; };
  }, [metadata, fromDay, throughDay]);

  const callTypes = useMemo(() => [...new Set(records.map((record) => record.callType).filter(Boolean))].sort(), [records]);
  const streets = useMemo(() => [...new Set(records.map((record) => [record.streetName, record.streetType].filter(Boolean).join(" ")).filter(Boolean))].sort(), [records]);
  const filtered = useMemo(() => records.filter((record) => {
    const fullStreet = [record.streetName, record.streetType].filter(Boolean).join(" ");
    return (!callType || record.callType === callType) && (!street || fullStreet === street);
  }), [records, callType, street]);
  const frequencyChart = useMemo(() => {
    const counts = new Map();
    for (const record of filtered) counts.set(record.callType || "Unspecified", (counts.get(record.callType || "Unspecified") || 0) + 1);
    const entries = [...counts].sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0])).slice(0, 12);
    return {
      labels: entries.map(([label]) => titleCaseCallType(label)),
      series: [{ key: "frequent-calls", label: "Published calls", valueType: "count", color: "#2563eb", data: entries.map(([, value]) => value) }],
    };
  }, [filtered]);
  const tableRows = useMemo(() => filtered.map((record, index) => ({
    id: `${record.day}-${record.cfsNumber || index}`,
    receivedDateTime: record.receivedDateTime,
    cfsNumber: record.cfsNumber || "—",
    street: titleCaseCallType([record.streetName, record.streetType].filter(Boolean).join(" ")) || "—",
    callType: titleCaseCallType(record.callType) || "—",
  })), [filtered]);
  const tableColumns = useMemo(() => [
    { key: "receivedDateTime", label: "Received", headerAlign: "left", style: { width: "190px" }, render: (row) => formatBlotterDateTime(row.receivedDateTime) },
    { key: "cfsNumber", label: "CFS #", headerAlign: "left", style: { width: "150px" } },
    { key: "street", label: "Street", headerAlign: "left", style: { width: "240px" } },
    { key: "callType", label: "Call type", headerAlign: "left", style: { width: "240px" } },
  ], []);

  return (
    <section className="space-y-8 sm:space-y-10">
      <div className="space-y-3 sm:space-y-4">
        <img src={holidayBanner} alt="Watercolor illustration of Glastonbury police, cadets, and community members with Santa at a holiday event" className="h-auto w-full object-contain" />
        <ReportPageHeader editorial title="Police Blotter" headingAs="h1" />
      </div>
      <p className="max-w-5xl text-base leading-7 text-slate-600">Review the calls for service published by Glastonbury Police. Choose a date range, call type, or street to explore daily activity, see the most frequent calls, and read individual entries. The blotter includes requests for assistance and routine police work as well as crime-related reports.</p>
      <ReportChapterHeader title="Explore published activity" headingAs="h2" tone="lavender"
        image={halloweenPatrolBanner} imageAlt="Watercolor illustration of a Glastonbury officer beside a patrol vehicle decorated for a Halloween community event"
        description="The blotter starts with the latest seven published days. Adjust the filters below to explore another period of up to one year, or narrow the results to a call type or street." />
      <div className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 xl:items-end">
          <label className="text-sm font-semibold text-slate-800">From date
            <input aria-label="Blotter from date" type="date" min={metadata?.firstDay} max={throughDay || metadata?.lastDay} value={fromDay} onChange={(event) => {
              const nextFromDay = event.target.value;
              setFromDay(nextFromDay);
              if (throughDay && throughDay > shiftDay(nextFromDay, MAX_BLOTTER_DAYS - 1)) setThroughDay(shiftDay(nextFromDay, MAX_BLOTTER_DAYS - 1));
            }} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal" />
          </label>
          <label className="text-sm font-semibold text-slate-800">Through date
            <input aria-label="Blotter through date" type="date" min={fromDay || metadata?.firstDay} max={fromDay ? [metadata?.lastDay, shiftDay(fromDay, MAX_BLOTTER_DAYS - 1)].filter(Boolean).sort()[0] : metadata?.lastDay} value={throughDay} onChange={(event) => setThroughDay(event.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal" />
          </label>
          <label className="text-sm font-semibold text-slate-800">Call type
            <select aria-label="Blotter call type" value={callType} onChange={(event) => setCallType(event.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal">
              <option value="">All call types</option>
              {callTypes.map((value) => <option key={value} value={value}>{titleCaseCallType(value)}</option>)}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-800">Street
            <select aria-label="Blotter street" value={street} onChange={(event) => setStreet(event.target.value)} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal">
              <option value="">All streets</option>
              {streets.map((value) => <option key={value} value={value}>{titleCaseCallType(value)}</option>)}
            </select>
          </label>
        </div>
        {status.startsWith("loading") && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">Loading Police Blotter…</div>}
        {status === "error" && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">Police Blotter could not be loaded: {error}</div>}
        {status === "loaded" && <>
          <div className="grid gap-3 sm:grid-cols-2">
            <MetricCard label="Published calls" value={filtered.length} explanation="Calls for service published by the Police Department for the selected dates, call type, and street." />
            <MetricCard label="Days loaded" value={daysInclusive(fromDay, throughDay, MAX_BLOTTER_DAYS).length} explanation="Each selected calendar day is stored and read as one compact Firebase document. A date range can include no more than 365 days." />
          </div>
          {filtered.length > 0 ? <>
            <LineChartWithToggles labels={frequencyChart.labels} series={frequencyChart.series} title="Most Frequent Calls for Service" showSeriesToggles={false} showChartTypeSelector={false} showLegend={false} initialChartType="bar" defaultYAxisMode="zero" xAxisTitle="Call type" yAxisTitle="Published calls" />
            <ReportChapterHeader title="Police Blotter Entries" headingAs="h2" tone="orange"
              image={trunkTreatBanner} imageAlt="Watercolor illustration of a festive police trunk-or-treat display with a decorative skeleton, pumpkins, and candy"
              description="Browse the published entries for your selected filters. Search or sort the table to find a call, and review its received time, street, and disposition." />
            <section className="overflow-hidden rounded-lg border border-slate-200">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3"><h2 className="text-lg font-semibold text-slate-950">Police Blotter</h2></div>
              <div className="p-2">
                <CommonTable
                  columns={tableColumns}
                  data={tableRows}
                  headerColor="bg-slate-100"
                  showAggregateTotal={false}
                  filterEmptyRows={false}
                  defaultSortField="receivedDateTime"
                  defaultSortOrder="desc"
                />
              </div>
            </section>
          </> : <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-700">No published calls match these filters.</div>}
        </>}
      </div>
    </section>
  );
}

function StolenVehicleMap({ locations }) {
  return (
    <div className="h-[280px] sm:h-[430px] overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-inner" data-testid="stolen-vehicle-map">
      <MapContainer center={[41.701, -72.59]} zoom={12} preferCanvas className="h-full w-full" scrollWheelZoom={false}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitOffenseMap locations={locations} />
        {locations.map((location) => (
          <CircleMarker key={`${location.latitude},${location.longitude}`} center={[location.latitude, location.longitude]}
            radius={Math.min(14, 6 + Math.log2(location.vehicles.length + 1) * 2)}
            pathOptions={{ color: "#1e3a8a", fillColor: "#2563eb", fillOpacity: 0.7, weight: 1.5 }}>
            <Popup><div className="min-w-44 text-sm"><div className="font-semibold text-slate-950">{titleCaseCallType(location.streetName) || "Published location"}</div><div>{location.vehicles.length.toLocaleString()} stolen {location.vehicles.length === 1 ? "vehicle" : "vehicles"}</div>{location.vehicles.slice(0, 4).map(vehicle => <div key={vehicle.id} className="mt-1 text-xs text-slate-600">{[vehicle.vehicleYear, vehicle.vehicleMake, vehicle.vehicleModel].filter(Boolean).join(" ") || "Vehicle details not published"}</div>)}</div></Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

function StolenVehicleListReport() {
  const [metadata, setMetadata] = useState(null);
  const [fromYear, setFromYear] = useSharedFilter("CrimeDashboard.jsx:StolenVehicleListReport:fromYear", null);
  const [throughYear, setThroughYear] = useSharedFilter("CrimeDashboard.jsx:StolenVehicleListReport:throughYear", null);
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState("loading-index");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getDoc(doc(db, STOLEN_VEHICLE_META_COLLECTION, STOLEN_VEHICLE_META_ID)).then((snapshot) => {
      if (!snapshot.exists()) throw new Error("Published stolen vehicle index was not found");
      if (!active) return;
      const value = snapshot.data();
      setMetadata(value); setFromYear.initialize(value.lastYear); setThroughYear.initialize(value.lastYear); setStatus("idle");
    }).catch((requestError) => { if (active) { setError(requestError.message); setStatus("error"); } });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!metadata || fromYear == null || throughYear == null) return;
    const years = metadata.years.filter(year => year >= fromYear && year <= throughYear);
    let active = true; setStatus("loading-records"); setError("");
    Promise.all(years.map(year => getDoc(doc(db, STOLEN_VEHICLE_COLLECTION, String(year)))))
      .then((snapshots) => {
        if (!active) return;
        setRecords(decodeStolenVehicleYears(snapshots.filter(snapshot => snapshot.exists()).map(snapshot => snapshot.data())));
        setStatus("loaded");
      }).catch((requestError) => { if (active) { setError(requestError.message); setStatus("error"); } });
    return () => { active = false; };
  }, [metadata, fromYear, throughYear]);

  const locations = useMemo(() => {
    const grouped = new Map();
    for (const record of records) {
      if (!Number.isFinite(record.latitude) || !Number.isFinite(record.longitude)) continue;
      const key = `${record.latitude},${record.longitude}`;
      if (!grouped.has(key)) grouped.set(key, { latitude: record.latitude, longitude: record.longitude, streetName: record.streetName, vehicles: [] });
      grouped.get(key).vehicles.push(record);
    }
    return [...grouped.values()];
  }, [records]);
  const tableRows = useMemo(() => records.map(record => ({
    id: record.id, cfsNumber: record.cfsNumber || "—", reportedDate: record.reportedDate || "—",
    streetName: titleCaseCallType(record.streetName) || "—", vehicleYear: record.vehicleYear || "—",
    vehicleMake: titleCaseCallType(record.vehicleMake) || "—", vehicleModel: record.vehicleModel || "—",
  })), [records]);
  const columns = useMemo(() => [
    { key: "cfsNumber", label: "CFS #", headerAlign: "left", style: { width: "145px" } },
    { key: "reportedDate", label: "Reported date", headerAlign: "left", style: { width: "140px" } },
    { key: "streetName", label: "Street name", headerAlign: "left", style: { width: "190px" } },
    { key: "vehicleYear", label: "Vehicle year", headerAlign: "left", style: { width: "120px" } },
    { key: "vehicleMake", label: "Vehicle make", headerAlign: "left", style: { width: "180px" } },
    { key: "vehicleModel", label: "Vehicle model", headerAlign: "left", style: { width: "250px" } },
  ], []);

  return (
    <section className="space-y-8 sm:space-y-10">
      <div className="space-y-3 sm:space-y-4">
        <img src={motorcyclesBanner} alt="Watercolor illustration of police motorcycles and officers at a Glastonbury community event" className="h-auto w-full object-contain" />
        <ReportPageHeader editorial title="Stolen Vehicle List" headingAs="h1" />
      </div>
      <p className="max-w-5xl text-base leading-7 text-slate-600">Explore vehicles reported stolen in the records published by Glastonbury Police. Choose a year range to see reported totals and locations, then review the dates, streets, and vehicle details in the list below. Multiple vehicles can be recorded under one call-for-service number.</p>
      <ReportChapterHeader title="Reported vehicles and locations" headingAs="h2" tone="lavender"
        description="The report starts with the latest published year. Select a range to compare records across years. Vehicles reported at the same coordinates share a map marker; records without published coordinates do not appear on the map." />
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2 sm:justify-end lg:grid-cols-[180px_180px]">
          <label className="text-sm font-semibold text-slate-800">From year
            <select aria-label="Stolen vehicles from year" value={fromYear ?? ""} onChange={event => setFromYear(Number(event.target.value))} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal">{metadata?.years.map(year => <option key={year} value={year}>{year}</option>)}</select>
          </label>
          <label className="text-sm font-semibold text-slate-800">Through year
            <select aria-label="Stolen vehicles through year" value={throughYear ?? ""} onChange={event => setThroughYear(Number(event.target.value))} className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 font-normal">{metadata?.years.map(year => <option key={year} value={year} disabled={fromYear != null && year < fromYear}>{year}</option>)}</select>
          </label>
        </div>
        {status.startsWith("loading") && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">Loading stolen vehicle data…</div>}
        {status === "error" && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">Stolen vehicle data could not be loaded: {error}</div>}
        {status === "loaded" && <>
          <div className="grid gap-3 sm:grid-cols-2">
            <MetricCard label="Total stolen vehicles" value={records.length} explanation="The Police Department’s published count of stolen vehicle occurrences in the selected received years. One CFS number can include more than one vehicle." />
            <MetricCard label="Distinct map points" value={locations.length} explanation="Unique coordinates already published by the Police Department for the selected stolen vehicle reports. Multiple vehicles at one coordinate share a marker." />
          </div>
          {locations.length ? <StolenVehicleMap locations={locations} /> : <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-700">No published map coordinates are available for this period.</div>}
          <OffenseDensityMap points={records} category="Stolen Vehicles" stolenVehicles />
          <img src={stationVisitBanner} alt="Watercolor illustration of Glastonbury officers and young scouts visiting the police station’s history display" className="h-auto w-full object-contain" loading="lazy" />
          <section className="overflow-hidden rounded-lg border border-slate-200">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3"><h2 className="text-lg font-semibold text-slate-950">Stolen Vehicle List</h2></div>
            <div className="p-2"><CommonTable columns={columns} data={tableRows} headerColor="bg-slate-100" showAggregateTotal={false} filterEmptyRows={false} defaultSortField="reportedDate" defaultSortOrder="desc" /></div>
          </section>
        </>}
      </div>
    </section>
  );
}

function rankedPieItems(records, valueFor) {
  const counts = new Map();
  for (const record of records) {
    const label = valueFor(record) || "Not published";
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  const ranked = [...counts].sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
  return ranked.map(([label, value]) => ({ key: label, label: titleCaseCallType(label), value, valueType: "count" }));
}

function StolenVehicleTrendsReport() {
  const [metadata, setMetadata] = useState(null);
  const [records, setRecords] = useState([]);
  const [visibleTrendYearKeys, setVisibleTrendYearKeys] = useState([]);
  const [status, setStatus] = useState("loading-index");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getDoc(doc(db, STOLEN_VEHICLE_META_COLLECTION, STOLEN_VEHICLE_META_ID)).then((snapshot) => {
      if (!snapshot.exists()) throw new Error("Published stolen vehicle index was not found");
      if (!active) return;
      const value = snapshot.data();
      const publishedYears = (value.years || []).filter(year => year >= 2014 && year <= new Date().getFullYear());
      setMetadata(value); setVisibleTrendYearKeys(publishedYears.slice(-3).map(String)); setStatus("idle");
    }).catch((requestError) => { if (active) { setError(requestError.message); setStatus("error"); } });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!metadata) return;
    const years = (metadata.years || []).filter(year => year >= 2014 && year <= new Date().getFullYear());
    let active = true; setStatus("loading-records"); setError("");
    Promise.all(years.map(year => getDoc(doc(db, STOLEN_VEHICLE_COLLECTION, String(year)))))
      .then((snapshots) => {
        if (!active) return;
        setRecords(decodeStolenVehicleYears(snapshots.filter(snapshot => snapshot.exists()).map(snapshot => snapshot.data())));
        setStatus("loaded");
      }).catch((requestError) => { if (active) { setError(requestError.message); setStatus("error"); } });
    return () => { active = false; };
  }, [metadata]);

  const selectedYears = useMemo(() => metadata?.years.filter(year => year >= 2014 && year <= new Date().getFullYear()) || [], [metadata]);
  const monthlyByYear = useMemo(() => {
    const counts = new Map(selectedYears.map(year => [year, Array(12).fill(0)]));
    const lastMonth = new Map();
    for (const record of records) {
      const month = Number(record.sourceDay?.slice(5, 7)) - 1;
      if (counts.has(record.receivedYear) && month >= 0 && month < 12) {
        counts.get(record.receivedYear)[month]++;
        lastMonth.set(record.receivedYear, Math.max(lastMonth.get(record.receivedYear) ?? -1, month));
      }
    }
    return {
      labels: MONTH_LABELS,
      series: selectedYears.map((year, index) => ({
        key: String(year), label: String(year), valueType: "count", color: TREND_COLORS[index % TREND_COLORS.length],
        data: counts.get(year).map((value, month) => year === metadata?.lastYear && month > (lastMonth.get(year) ?? -1) ? null : value),
      })),
    };
  }, [records, selectedYears, metadata]);
  const visibleTrendYearSet = useMemo(() => new Set(visibleTrendYearKeys.map(Number)), [visibleTrendYearKeys]);
  const visibleTrendRecords = useMemo(() => records.filter(record => visibleTrendYearSet.has(record.receivedYear)), [records, visibleTrendYearSet]);
  const monthlyAggregate = useMemo(() => {
    const counts = Array(12).fill(0);
    for (const record of visibleTrendRecords) {
      const month = Number(record.sourceDay?.slice(5, 7)) - 1;
      if (month >= 0 && month < 12) counts[month]++;
    }
    return { labels: MONTH_LABELS, series: [{ key: "monthly-total", label: "Stolen vehicles", valueType: "count", color: "#2563eb", data: counts }] };
  }, [visibleTrendRecords]);
  const annual = useMemo(() => {
    const counts = new Map(selectedYears.map(year => [year, 0]));
    for (const record of records) counts.set(record.receivedYear, (counts.get(record.receivedYear) || 0) + 1);
    return { labels: selectedYears.map(String), series: [{ key: "annual-total", label: "Stolen vehicles", valueType: "count", color: "#2563eb", data: selectedYears.map(year => counts.get(year) || 0) }] };
  }, [records, selectedYears]);
  const makeItems = useMemo(() => rankedPieItems(records.filter(record => record.vehicleMake && record.vehicleMake.trim().toLowerCase() !== "not published"), record => record.vehicleMake), [records]);
  const streetItems = useMemo(() => rankedPieItems(records, record => record.streetName), [records]);
  const visibleMonthCount = useMemo(() => monthlyByYear.series
    .filter(series => visibleTrendYearKeys.includes(series.key))
    .reduce((total, series) => total + series.data.filter(value => value !== null).length, 0), [monthlyByYear, visibleTrendYearKeys]);

  return (
    <section className="space-y-8 sm:space-y-10">
      <div className="space-y-3 sm:space-y-4">
        <img src={fireStationBanner} alt="Watercolor illustration of a Glastonbury fire station and ladder truck at a community car-seat safety event" className="h-auto w-full object-contain" />
        <ReportPageHeader editorial title="Stolen Vehicle Trends" headingAs="h1" />
      </div>
      <p className="max-w-5xl text-base leading-7 text-slate-600">Explore patterns in the stolen vehicle records published by Glastonbury Police. Compare activity by year and month, then review the vehicle makes and streets most often recorded. These charts count reported vehicle occurrences; one call-for-service number can include more than one vehicle.</p>
      <div className="space-y-5">
        {status.startsWith("loading") && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">Loading stolen vehicle trends…</div>}
        {status === "error" && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">Stolen vehicle trends could not be loaded: {error}</div>}
        {status === "loaded" && <>
          <ReportChapterHeader title="Activity over time" headingAs="h2" tone="lavender"
            description="See annual totals, then use the year toggles on the monthly comparison to explore seasonal patterns. The cards and monthly total below follow those selected years." />
          <LineChartWithToggles labels={annual.labels} series={annual.series} title="Stolen Vehicles by Year" showSeriesToggles={false} showLegend={false} initialChartType="bar" defaultYAxisMode="zero" xAxisTitle="Received year" yAxisTitle="Stolen vehicles" />
          <LineChartWithToggles labels={monthlyByYear.labels} series={monthlyByYear.series} title="Stolen Vehicles by Month" showSeriesToggles toggleUIMode="inline" defaultVisibleKeys={selectedYears.slice(-3).map(String)} onVisibleKeysChange={setVisibleTrendYearKeys} initialChartType="line" defaultYAxisMode="zero" xAxisTitle="Month" yAxisTitle="Stolen vehicles" />
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard label="Stolen vehicles" value={visibleTrendRecords.length} explanation="Published stolen vehicle occurrences for the years currently selected in the monthly comparison." />
            <MetricCard label="Years compared" value={visibleTrendYearKeys.length} explanation="The number of years currently selected in the monthly comparison." />
            <MetricCard label="Monthly average" value={visibleMonthCount ? Math.round(visibleTrendRecords.length / visibleMonthCount) : 0} explanation="Average published stolen vehicle occurrences per available month across the selected years." />
          </div>
          <LineChartWithToggles labels={monthlyAggregate.labels} series={monthlyAggregate.series} title="Monthly Stolen Vehicle Total" showSeriesToggles={false} showLegend={false} initialChartType="area" defaultYAxisMode="zero" xAxisTitle="Month" yAxisTitle="Stolen vehicles" />
          <ReportChapterHeader title="Vehicle makes and reported streets" headingAs="h2" tone="orange"
            image={outreachBanner} imageAlt="Watercolor illustration of a community outreach event outside Glastonbury Town Hall"
            description="Explore the most common vehicle makes and most frequently reported streets across the full published history loaded on this page. These breakdowns use all available years, independently of the monthly chart’s year selection." />
          <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
            <PieChartWithToggles title="Most Common Vehicle Makes" items={makeItems} showSeriesToggles={false} showChartTypeSelector={false} showLegend={false} showTotalValuesSelector defaultTotalValues={10} showOutsideLabels titleBar sliceOrder="valueDesc" />
            <PieChartWithToggles title="Most Frequent Streets" items={streetItems} showSeriesToggles={false} showChartTypeSelector={false} showLegend={false} showTotalValuesSelector defaultTotalValues={10} showOutsideLabels titleBar sliceOrder="valueDesc" />
          </div>
        </>}
      </div>
    </section>
  );
}

function CrimeTypeRangeSummary({ summary }) {
  const [comparison, setComparison] = useState(null);
  const [comparisonStatus, setComparisonStatus] = useState("idle");
  const [comparisonError, setComparisonError] = useState("");
  const [metadata, setMetadata] = useState(null);
  const [fromDay, setFromDay] = useSharedFilter("CrimeDashboard.jsx:CrimeTypeRangeSummary:fromDay", "");
  const [throughDay, setThroughDay] = useSharedFilter("CrimeDashboard.jsx:CrimeTypeRangeSummary:throughDay", "");
  const [records, setRecords] = useState([]);
  const [status, setStatus] = useState("loading-index");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getDoc(doc(db, BLOTTER_META_COLLECTION, BLOTTER_META_ID)).then((snapshot) => {
      if (!snapshot.exists()) throw new Error("Published Police Blotter index was not found");
      if (!active) return;
      const value = snapshot.data();
      setMetadata(value);
      setFromDay.initialize(shiftDay(value.lastDay, -6));
      setThroughDay.initialize(value.lastDay);
      setStatus("idle");
    }).catch((requestError) => {
      if (active) { setError(requestError.message); setStatus("error"); }
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!metadata) return;
    if (!fromDay || !throughDay || fromDay > throughDay || fromDay < metadata.firstDay || throughDay > metadata.lastDay) {
      setStatus("error"); setError("Choose valid dates within published coverage."); return;
    }
    const days = daysInclusive(fromDay, throughDay, MAX_BLOTTER_DAYS + 1);
    if (days.length > MAX_BLOTTER_DAYS || days.at(-1) !== throughDay) {
      setError("Choose a date range of 365 days or fewer.");
      setStatus("error");
      return;
    }
    let active = true;
    setRecords([]); setStatus("loading-records"); setError("");
    Promise.all(days.map((day) => getDoc(doc(db, BLOTTER_DAYS_COLLECTION, day))))
      .then((snapshots) => {
        if (!active) return;
        setRecords(decodeBlotterDays(snapshots.filter((snapshot) => snapshot.exists()).map((snapshot) => snapshot.data())));
        setStatus("loaded");
      })
      .catch((requestError) => {
        if (active) { setError(requestError.message); setStatus("error"); }
      });
    return () => { active = false; };
  }, [metadata, fromDay, throughDay]);

  useEffect(() => {
    setComparison(null); setComparisonError("");
    if (!metadata || !fromDay || !throughDay) { setComparisonStatus("idle"); return; }
    const range = comparisonRange(fromDay, throughDay, "previous");
    if (!range || range.from < metadata.firstDay || range.through > metadata.lastDay) {
      setComparisonStatus("error"); setComparisonError("Comparison dates are outside published coverage."); return;
    }
    let active = true;
    setComparisonStatus("loading");
    Promise.all(daysInclusive(range.from, range.through, 365).map(day => getDoc(doc(db, BLOTTER_DAYS_COLLECTION, day))))
      .then(snapshots => {
        if (!active) return;
        setComparison({ range, records: decodeBlotterDays(snapshots.filter(snapshot => snapshot.exists()).map(snapshot => snapshot.data())) });
        setComparisonStatus("loaded");
      }).catch(err => { if (active) { setComparisonError(err.message); setComparisonStatus("error"); } });
    return () => { active = false; };
  }, [fromDay, throughDay, metadata]);

  const items = useMemo(() => rankedPieItems(records, (record) => record.callType || "Unspecified"), [records]);

  return (
    <section className="space-y-5">
      <ReportChapterHeader title="Crime and Activity by Date Range" headingAs="h2" tone="lavender"
        image={coffeeBanner} imageAlt="Watercolor illustration of a Glastonbury police officer serving coffee at a community coffee shop"
        description="Choose a date range to compare the Police Department’s published activity types. Explore the most common calls and the comparisons below; the selected period applies to this section." />
      <div className="space-y-5">
        {metadata && fromDay && throughDay && <TrendDateRange fromDay={fromDay} throughDay={throughDay} metadata={metadata} onApply={(from, through) => { setFromDay(from); setThroughDay(through); }} />}
        {comparisonStatus === "loading" && <p role="status" className="text-sm text-blue-900">Loading comparison…</p>}
        {comparisonStatus === "error" && <p role="alert" className="text-sm text-red-700">Could not load comparison: {comparisonError}</p>}
        {status.startsWith("loading") && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-medium text-blue-900">Loading crime and activity types…</div>}
        {status === "error" && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900">Crime and activity types could not be loaded: {error}</div>}
        {status === "loaded" && <TrendPanel title="Most Common Crime and Activity Types">
        {items.length ? (
          <div className="mx-auto w-full lg:w-3/4" data-testid="crime-type-range-pie-wrapper">
            <PieChartWithToggles
              title="Most Common Crime and Activity Types"
              items={items}
              showSeriesToggles={false}
              showChartTypeSelector={false}
              showLegend={false}
              showTotalValuesSelector
              defaultTotalValues={10}
              showOutsideLabels
              titleBar
              sliceOrder="valueDesc"
            />
          </div>
        ) : <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-700">No published police activity is available for this date range.</div>}
        </TrendPanel>}
        {status === "loaded" && <CrimeTrendInsights key={`${fromDay}/${throughDay}`} records={records} summary={summary} comparison={comparison} comparisonStatus={comparisonStatus} fromDay={fromDay} throughDay={throughDay} />}
      </div>
    </section>
  );
}

function annualRowsFor(summary, callType, years) {
  const observed = firstObservedMonth(summary, callType);
  const observedYear = observed ? Number(observed.slice(0, 4)) : null;
  const observedMonth = observed ? Number(observed.slice(5, 7)) : 1;
  return years.map((year) => {
    if (observedYear == null || year < observedYear) return null;
    const startMonth = year === observedYear ? observedMonth : 1;
    const endMonth = completeMonthCount(summary, year);
    if (endMonth < startMonth) return null;
    return {
      year,
      startMonth,
      endMonth,
      total: totalForPeriod(summary, callType, year, endMonth, startMonth),
    };
  }).filter(Boolean);
}

function CallTypeDetail({ summary, callType, chart, years }) {
  const latestYear = years.at(-1);
  const [firstYear, setFirstYear] = useSharedFilter("CrimeDashboard.jsx:CallTypeDetail:firstYear:" + [callType].join(":"), years.includes(2022) ? 2022 : years[0]);
  const [secondYear, setSecondYear] = useSharedFilter("CrimeDashboard.jsx:CallTypeDetail:secondYear:" + [callType].join(":"), latestYear);
  const series = useMemo(
    () => chart.series.filter((item) => item.key === callType),
    [chart.series, callType],
  );
  const comparison = useMemo(
    () => compareYears(summary, callType, firstYear, secondYear),
    [summary, callType, firstYear, secondYear],
  );
  const annualRows = useMemo(
    () => annualRowsFor(summary, callType, years),
    [summary, callType, years],
  );
  const annualChart = useMemo(() => ({
    labels: annualRows.map((row) => String(row.year)),
    series: [{
      key: `${callType}-annual-total`,
      label: "Published calls",
      data: annualRows.map((row) => row.total),
      valueType: "count",
      color: "#2563eb",
    }],
  }), [annualRows, callType]);
  const hasComparison = comparison.first != null && comparison.second != null;

  return (
    <div className="space-y-5">
      <LineChartWithToggles
        labels={chart.labels}
        series={series}
        title="Monthly calls"
        showSeriesToggles={false}
        showLegend={false}
        initialChartType="area"
        defaultYAxisMode="zero"
        xTrimMode="visibleData"
        xAxisTitle="Month"
        yAxisTitle="Published calls"
      />

      <section>
        <LineChartWithToggles
          labels={annualChart.labels}
          series={annualChart.series}
          title="Annual totals"
          showSeriesToggles={false}
          showChartTypeSelector={false}
          showLegend={false}
          initialChartType="bar"
          defaultYAxisMode="zero"
          xAxisTitle="Year"
          yAxisTitle="Published calls"
        />
      </section>

      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Compare years</h3>
            <p className="text-xs text-gray-500">Current-year comparisons use the same completed months in both years.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="font-medium text-gray-700">
              First year
              <select aria-label={`${callType} first comparison year`} value={firstYear} onChange={(event) => setFirstYear(Number(event.target.value))} className="ml-2 rounded border border-gray-300 bg-white px-2 py-1.5">
                {years.map((year) => <option key={year} value={year}>{year}</option>)}
              </select>
            </label>
            <label className="font-medium text-gray-700">
              Second year
              <select aria-label={`${callType} second comparison year`} value={secondYear} onChange={(event) => setSecondYear(Number(event.target.value))} className="ml-2 rounded border border-gray-300 bg-white px-2 py-1.5">
                {years.map((year) => <option key={year} value={year}>{year}</option>)}
              </select>
            </label>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div><div className="text-xs text-gray-500">{firstYear}</div><div className="text-2xl font-bold">{comparison.first ?? "—"}</div></div>
          <div><div className="text-xs text-gray-500">{secondYear}</div><div className="text-2xl font-bold">{comparison.second ?? "—"}</div></div>
          <div><div className="text-xs text-gray-500">Change</div><div className={`text-2xl font-bold ${comparison.difference ? "text-blue-700" : "text-gray-700"}`}>{hasComparison ? `${comparison.difference > 0 ? "+" : ""}${comparison.difference}` : "—"}</div></div>
        </div>
        <p className="mt-3 text-center text-xs text-gray-500">
          {hasComparison ? `${monthSpanLabel(comparison.startMonth, comparison.endMonth)} · ${comparison.percentChange == null ? "No percentage available" : `${comparison.percentChange > 0 ? "+" : ""}${comparison.percentChange.toFixed(1)}%`}` : "This call type was not tracked in both selected years"}
        </p>
      </section>

    </div>
  );
}

function CallTypeSection({ summary, callType, chart, years }) {
  const headingId = `crime-${callType.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const [collapsed, setCollapsed] = useState(() => !shouldOpenSharedSection(`${headingId}-content`));
  return (
    <section className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
      <button
        type="button"
        id={headingId}
        aria-controls={`${headingId}-content`}
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((previous) => !previous)}
        className="w-full bg-gray-100 px-3 py-2 font-semibold text-lg text-gray-900 flex justify-between items-center text-left rounded-none hover:bg-gray-200"
      >
        {titleCaseCallType(callType)}
        <span aria-hidden="true">{collapsed ? "▶" : "▼"}</span>
      </button>
      {!collapsed && (
        <div id={`${headingId}-content`} className="bg-white p-4" aria-labelledby={headingId}>
          <CallTypeDetail summary={summary} callType={callType} chart={chart} years={years} />
        </div>
      )}
    </section>
  );
}

export function CrimeDashboard() {
  const location = useLocation();
  const selectedSection = crimeSectionFromSearch(location.search);
  const [summary, setSummary] = useState(null);
  const [typeSearch, setTypeSearch] = useSharedFilter("CrimeDashboard.jsx:CrimeDashboard:typeSearch", "");
  const [error, setError] = useState("");

  useEffect(() => {
    if (selectedSection !== "crime-trends" || summary) return undefined;
    let active = true;
    getDoc(doc(db, SUMMARY_COLLECTION, SUMMARY_DOCUMENT))
      .then((snapshot) => {
        if (!snapshot.exists()) throw new Error("Published police summary was not found");
        if (active) setSummary(snapshot.data());
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      });
    return () => { active = false; };
  }, [selectedSection, summary]);

  const chart = useMemo(() => summary ? buildCrimeSeries(summary) : { labels: [], series: [] }, [summary]);
  const years = useMemo(() => availableYears(summary), [summary]);
  const matchingTypes = (summary?.callTypes || []).filter(type =>
    `${type} ${titleCaseCallType(type)}`.toLowerCase().includes(typeSearch.trim().toLowerCase()));

  const selectedLabel = CRIME_SECTIONS.find((section) => section.id === selectedSection)?.label;

  let content;
  if (selectedSection === "offenses") {
    content = <OffenseReport />;
  } else if (selectedSection === "calls-for-service") {
    content = <CallsForServiceReport />;
  } else if (selectedSection === "police-blotter") {
    content = <PoliceBlotterReport />;
  } else if (selectedSection === "stolen-vehicle-list") {
    content = <StolenVehicleListReport />;
  } else if (selectedSection === "stolen-vehicle-trends") {
    content = <StolenVehicleTrendsReport />;
  } else if (selectedSection === "crime-trends" && error) {
    content = <div className="p-5 text-sm text-red-700">Crime data could not be loaded: {error}</div>;
  } else if (selectedSection === "crime-trends" && !summary) {
    content = <div className="p-5 text-sm text-gray-500">Loading police activity data…</div>;
  } else if (selectedSection === "crime-trends") {
    content = (
      <div className="space-y-8 sm:space-y-10">
        <div className="space-y-3 sm:space-y-4">
          <img src={officerGroupBanner} alt="Watercolor illustration of Glastonbury officers and cadets gathered by the park’s globe sculpture" className="h-auto w-full object-contain" />
          <ReportPageHeader editorial title="Crime Trends" headingAs="h1" />
        </div>
        <p className="max-w-5xl text-base leading-7 text-slate-600">Explore patterns in Glastonbury’s published police activity. Compare call types over a selected date range, then explore individual categories over time. These records include requests for assistance and other police activity as well as crime-related reports.</p>
        <CrimeTypeRangeSummary summary={summary} />
        <section className="space-y-5">
          <ReportChapterHeader title="Activity Trends Over Time" headingAs="h2" tone="orange"
            image={breakfastBanner} imageAlt="Watercolor illustration of Glastonbury police officers sharing breakfast in a local dining room"
            description="Find a call type and open its report to explore monthly history, compare years, and review annual totals. Each report has its own year controls and uses the published history available for that category." />
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-slate-700">Find a call type
              <input type="search" value={typeSearch} onChange={event => setTypeSearch(event.target.value)} placeholder="Search, for example vandalism or motor vehicle" className="mt-2 block w-full rounded-lg border border-slate-300 px-4 py-3 font-normal focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600" />
            </label>
            <p role="status" className="text-sm text-slate-500">{matchingTypes.length} of {summary.callTypes.length} call types · Full published history</p>
            {!matchingTypes.length && <div className="rounded-lg bg-slate-50 p-5 text-sm text-slate-700">No call types match your search. <button type="button" onClick={() => setTypeSearch("")} className="font-semibold text-blue-800 underline">Clear search</button></div>}
            {matchingTypes.map((callType) => (
              <CallTypeSection
                key={callType}
                summary={summary}
                callType={callType}
                chart={chart}
                years={years}
              />
            ))}
          </div>
        </section>
      </div>
    );
  } else {
    content = (
      <section className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-md">
        <h1 className="text-2xl font-bold text-slate-950">{selectedLabel}</h1>
        <p className="mt-2 text-sm text-slate-600">This police dashboard section is ready for its next report.</p>
      </section>
    );
  }

  return (
    <div className="min-h-screen bg-base-200 text-base-content flex flex-col">
      <div className="flex flex-1 w-full justify-center">
        <div className="w-full max-w-screen-2xl">
          <BudgetCard>
            {content}
          </BudgetCard>
        </div>
      </div>
    </div>
  );
}
